// The common object passed to the SDK Client
export interface GamePlayer {
  playerId: string;
  registered: boolean;
  entryPayload: Record<string, unknown>;
  playerData: Record<string, unknown>;
}

export function returnWelcome() {
  return `Welcome from common!`;
}

export type PlayerData = Record<string, unknown>;

export interface PlayerDataUpdate {
  update: Partial<PlayerData>;
}

export type GamePageContext = {
  gameSlug: string;
  gameId: string;
  gameType: string;
  playerId: string;
  env: string;
  entryPayload: Record<string, unknown>;
  registered: boolean;
  gameVersionId: string;
  analyticsId: string;
};

/**
 * Deep merges two objects
 * @param target The target object to merge into
 * @param source The source object to merge from
 * @returns A new object with properties from both target and source, with source taking precedence
 */
export function applyStateUpdate(
  playerData: PlayerData,
  update: PlayerDataUpdate,
): PlayerData {
  return { ...playerData, ...update.update };
}

export function isError(value: unknown): value is Error {
  return (
    value instanceof Error || // Standard Error object
    (typeof value === "object" && value !== null && "message" in value) // Compatible objects
  );
}

export function smsLink(phoneNumber: string, message: string) {
  return `sms:${phoneNumber}?body=${encodeURIComponent(message)}`;
}

export function doesBrowserSupportSMSLinks(opts: { userAgent: string }) {
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      opts.userAgent,
    );

  // Detect if this is a desktop Mac using userAgent
  const isMac = /Macintosh.*Mac OS X/i.test(opts.userAgent);
  return isMobile || isMac;
}

export class UnreachableError extends Error {
  constructor(message: string) {
    super(`Unreachable code reached: ${message}`);
  }
}

export function validateIsRecord(
  thing: unknown,
): thing is Record<string, unknown> {
  return (
    typeof thing === "object" &&
    thing !== null &&
    !Array.isArray(thing) &&
    !(thing instanceof Date)
  );
}

export function assertUnreachable(_x: never): never {
  throw new UnreachableError(`Unreachable code reached`);
}

// Escape any special characters in the keywords so regex matching works
export function escapeForRegex(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function checkMessageKeywordMatch(
  message: string,
  keywords: string[],
): boolean {
  const keywordMatchers = keywords.map((keyword) => {
    const escapedKeyword = escapeForRegex(keyword);

    // \\b word bounds don't work when token includes non-word characters like ! or $
    // use lookaheads that escape a non-word character or start/end of match string
    return `(?<![\\w+])${escapedKeyword}(?![\\w+])`;
  });
  const boundedKeywordRegex = new RegExp(`${keywordMatchers.join(".*")}`, "i");

  return boundedKeywordRegex.test(message);
}

export * from "./bridge";
export * from "./flatten-posthog-properties";
